import React, { useState, useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from 'react';
import { useMacros } from '../../context/MacrosContext';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import {
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Typography,
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  IconButton,
  Paper,
  Divider,
  Tooltip,
  Switch
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  FormatBold as FormatBoldIcon,
  FormatItalic as FormatItalicIcon,
  FormatUnderlined as FormatUnderlineIcon,
  FormatListBulleted as FormatListBulletedIcon,
  FormatListNumbered as FormatListNumberedIcon
} from '@mui/icons-material';

// Rich text formatting toolbar - only shown in rich text mode
const RichTextToolbar = ({ editor }) => {
  if (!editor) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, p: 0.5, borderBottom: '1px solid #e0e0e0', backgroundColor: '#fafafa' }}>
      <IconButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={!editor.can().chain().focus().toggleBold().run()}
        color={editor.isActive('bold') ? 'primary' : 'default'}
        size="small"
        sx={{ p: 0.5 }}
      >
        <FormatBoldIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <IconButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={!editor.can().chain().focus().toggleItalic().run()}
        color={editor.isActive('italic') ? 'primary' : 'default'}
        size="small"
        sx={{ p: 0.5 }}
      >
        <FormatItalicIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <IconButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        disabled={!editor.can().chain().focus().toggleUnderline().run()}
        color={editor.isActive('underline') ? 'primary' : 'default'}
        size="small"
        sx={{ p: 0.5 }}
      >
        <FormatUnderlineIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 20, alignSelf: 'center' }} />
      <IconButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        color={editor.isActive('bulletList') ? 'primary' : 'default'}
        size="small"
        sx={{ p: 0.5 }}
      >
        <FormatListBulletedIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <IconButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        color={editor.isActive('orderedList') ? 'primary' : 'default'}
        size="small"
        sx={{ p: 0.5 }}
      >
        <FormatListNumberedIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );
};

const EnhancedTextEditor = forwardRef(({ 
  value = '', 
  onChange, 
  placeholder = 'Type /m for macros or @code to expand...',
  multiline = true,
  rows = 4,
  fullWidth = true,
  variant = 'outlined',
  size = 'medium',
  disabled = false,
  height = '200px',
  label,
  helperText,
  error,
  ...props 
}, ref) => {
  const { macros, loading } = useMacros();
  const [showMacroDialog, setShowMacroDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [triggerPosition, setTriggerPosition] = useState(-1);
  const [currentInput, setCurrentInput] = useState(value);
  const [isRichMode, setIsRichMode] = useState(false);
  const textFieldRef = useRef(null);
  const rowsPerPage = 15;

  // Rich text editor setup
  const editor = useEditor({
    extensions: [StarterKit, Underline],
    content: value,
    editorProps: {
      attributes: {
        style: `min-height: ${height}; overflow-y: auto; padding: 8px;`,
      },
    },
    onUpdate: ({ editor }) => {
      if (isRichMode && onChange) {
        onChange({ target: { value: editor.getHTML() } });
      }
    },
  });

  // Sync content when switching modes or when value changes externally
  useEffect(() => {
    if (isRichMode && editor && editor.getHTML() !== value) {
      editor.commands.setContent(value || '');
    } else if (!isRichMode) {
      setCurrentInput(value);
    }
  }, [value, isRichMode, editor]);



  // Filter macros based on search query
  const filteredMacros = macros.filter(macro => {
    const searchLower = searchQuery.toLowerCase();
    return (
      macro.code?.toLowerCase().includes(searchLower) ||
      macro.expansionText?.toLowerCase().includes(searchLower)
    );
  });

  // Paginate macros
  const paginatedMacros = filteredMacros.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );

  const totalPages = Math.ceil(filteredMacros.length / rowsPerPage);

  // Handle mode toggle
  const handleModeToggle = useCallback((richMode) => {
    if (richMode && !isRichMode) {
      // Switching to rich mode
      setIsRichMode(true);
      if (editor) {
        editor.commands.setContent(currentInput || '');
      }
    } else if (!richMode && isRichMode) {
      // Switching to plain mode
      setIsRichMode(false);
      const htmlContent = editor ? editor.getHTML() : '';
      // Convert HTML to plain text (simple conversion)
      const plainText = htmlContent.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
      setCurrentInput(plainText);
      if (onChange) {
        onChange({ target: { value: plainText } });
      }
    }
  }, [isRichMode, editor, currentInput, onChange]);

  // Handle text input changes (plain text mode)
  const handleTextChange = useCallback((event) => {
    const newValue = event.target.value;
    const cursorPos = event.target.selectionStart;
    
    setCurrentInput(newValue);
    setCursorPosition(cursorPos);

    // Check for /m trigger
    const beforeCursor = newValue.substring(0, cursorPos);
    const lastSlashM = beforeCursor.lastIndexOf('/m');
    
    if (lastSlashM !== -1 && lastSlashM < cursorPos) {
      const afterSlashM = beforeCursor.substring(lastSlashM + 2);
      if (afterSlashM.length === 0 || afterSlashM.startsWith(' ')) {
        setTriggerPosition(lastSlashM);
        setSearchQuery('');
        setCurrentPage(0);
        setShowMacroDialog(true);
      }
    }

    // Check for @code pattern
    const atPattern = /@(\w+)/g;
    let match;
    let newText = newValue;
    
    while ((match = atPattern.exec(newValue)) !== null) {
      const code = match[1];
      const macro = macros.find(m => m.code === code);
      if (macro) {
        newText = newText.replace(match[0], macro.expansionText);
      }
    }

    if (newText !== newValue) {
      onChange({ target: { value: newText } });
    } else {
      onChange(event);
    }
  }, [macros, onChange]);

  // Handle macro selection
  const handleMacroSelect = useCallback((macro) => {
    if (isRichMode && editor) {
      // Insert macro in rich text mode
      editor.chain().focus().insertContent(macro.expansionText).run();
    } else {
      // Insert macro in plain text mode
      if (triggerPosition !== -1) {
        const beforeTrigger = currentInput.substring(0, triggerPosition);
        const afterCursor = currentInput.substring(cursorPosition);
        const newValue = beforeTrigger + macro.expansionText + afterCursor;
        
        onChange({ target: { value: newValue } });
        setCurrentInput(newValue);
      }
    }
    
    setShowMacroDialog(false);
    setTriggerPosition(-1);
    setSearchQuery('');
    setCurrentPage(0);
  }, [isRichMode, editor, triggerPosition, currentInput, cursorPosition, onChange]);

  // Handle search in macro dialog
  const handleMacroSearch = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(0);
  };

  // Handle page change in macro dialog
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle key events
  const handleKeyDown = useCallback((event) => {
    if (showMacroDialog) {
      if (event.key === 'Escape') {
        setShowMacroDialog(false);
        setTriggerPosition(-1);
        event.preventDefault();
      } else if (event.key === 'Enter' && !event.shiftKey) {
        if (paginatedMacros.length > 0) {
          handleMacroSelect(paginatedMacros[0]);
        }
        event.preventDefault();
      }
    } else if (!isRichMode) {
      // Handle @code expansion on Tab or Enter in plain text mode
      if (event.key === 'Tab' || event.key === 'Enter') {
        const beforeCursor = currentInput.substring(0, cursorPosition);
        const atMatch = beforeCursor.match(/@(\w+)$/);
        
        if (atMatch) {
          const code = atMatch[1];
          const macro = macros.find(m => m.code === code);
          if (macro) {
            const beforeAt = currentInput.substring(0, beforeCursor.lastIndexOf('@'));
            const afterCursor = currentInput.substring(cursorPosition);
            const newValue = beforeAt + macro.expansionText + afterCursor;
            
            onChange({ target: { value: newValue } });
            setCurrentInput(newValue);
            event.preventDefault();
          }
        }
      }
    }
  }, [showMacroDialog, paginatedMacros, handleMacroSelect, isRichMode, currentInput, cursorPosition, macros, onChange]);

  // Expose methods to parent components
  useImperativeHandle(ref, () => ({
    getContent: () => {
      return isRichMode && editor ? editor.getHTML() : currentInput;
    },
    setContent: (content) => {
      if (isRichMode && editor) {
        editor.commands.setContent(content);
      } else {
        setCurrentInput(content);
        if (onChange) {
          onChange({ target: { value: content } });
        }
      }
    },
    focus: () => {
      if (isRichMode && editor) {
        editor.commands.focus();
      } else if (textFieldRef.current) {
        textFieldRef.current.focus();
      }
    }
  }));

  return (
    <Box>
      {label && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {label}
          </Typography>
          <Tooltip title={isRichMode ? 'Rich Text Mode' : 'Plain Text Mode'} placement="top">
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontSize: '0.7rem' }}>
                {isRichMode ? 'Rich' : 'Plain'}
              </Typography>
              <Switch
                checked={isRichMode}
                onChange={(e) => handleModeToggle(e.target.checked)}
                size="small"
                sx={{
                  width: 32,
                  height: 18,
                  padding: 0,
                  '& .MuiSwitch-switchBase': {
                    padding: 0,
                    margin: '2px',
                    transitionDuration: '200ms',
                    '&.Mui-checked': {
                      transform: 'translateX(14px)',
                      color: '#fff',
                      '& + .MuiSwitch-track': {
                        backgroundColor: '#667eea',
                        opacity: 1,
                      },
                    },
                  },
                  '& .MuiSwitch-thumb': {
                    width: 14,
                    height: 14,
                  },
                  '& .MuiSwitch-track': {
                    borderRadius: 9,
                    backgroundColor: '#bdbdbd',
                    opacity: 1,
                  },
                }}
              />
            </Box>
          </Tooltip>
        </Box>
      )}
      
      <Paper variant="outlined" sx={{ borderRadius: '4px', overflow: 'hidden' }}>
        {/* Only show formatting toolbar in rich text mode */}
        {isRichMode && <RichTextToolbar editor={editor} />}
        
        {isRichMode ? (
          <Box sx={{ minHeight: height, '& .ProseMirror': { outline: 'none', p: 1, fontSize: '0.875rem' } }}>
            <EditorContent editor={editor} />
          </Box>
        ) : (
          <TextField
            ref={textFieldRef}
            value={currentInput}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            multiline={multiline}
            rows={rows}
            fullWidth={fullWidth}
            variant="standard"
            size={size}
            disabled={disabled}
            error={error}
            InputProps={{
              disableUnderline: true,
              sx: { p: 1, fontSize: '0.875rem' }
            }}
            {...props}
          />
        )}
      </Paper>

      {helperText && (
        <Typography variant="caption" color={error ? 'error' : 'text.secondary'} sx={{ mt: 0.5, display: 'block' }}>
          {helperText}
        </Typography>
      )}

      {/* Macro Selection Dialog */}
      <Dialog 
        open={showMacroDialog} 
        onClose={() => setShowMacroDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '70vh'
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Select Macro</Typography>
            <Chip 
              label={`${filteredMacros.length} macros`} 
              size="small" 
              color="primary" 
              variant="outlined"
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Search macros..."
            value={searchQuery}
            onChange={handleMacroSearch}
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {paginatedMacros.map((macro) => (
                  <ListItem key={macro.id} disablePadding>
                    <ListItemButton onClick={() => handleMacroSelect(macro)}>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip 
                              label={macro.code} 
                              size="small" 
                              color="primary" 
                              variant="outlined"
                            />
                            <Typography variant="body2" color="text.secondary">
                              {macro.expansionText.length > 50 
                                ? `${macro.expansionText.substring(0, 50)}...` 
                                : macro.expansionText
                              }
                            </Typography>
                          </Box>
                        }
                        secondary={
                          macro.expansionText.length > 50 && (
                            <Typography variant="caption" color="text.secondary">
                              {macro.expansionText}
                            </Typography>
                          )
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
              
              {totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={2} gap={1}>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <Chip
                      key={i}
                      label={i + 1}
                      size="small"
                      color={currentPage === i ? "primary" : "default"}
                      onClick={() => handlePageChange(i)}
                      clickable
                    />
                  ))}
                </Box>
              )}
              
              {paginatedMacros.length === 0 && (
                <Box textAlign="center" p={3}>
                  <Typography variant="body2" color="text.secondary">
                    {searchQuery ? 'No macros found matching your search.' : 'No macros available.'}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
});

export default EnhancedTextEditor; 